"use server";

import { redirect } from "next/navigation";

import { env } from "@/env";
import { emailSpecTable, emailWrap } from "@/lib/email-theme";
import { sendMail } from "@/lib/mail";
import { rateLimit } from "@/lib/rate-limit";

export type ContactState = { error: string } | null;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const esc = (s: string) =>
  s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]!);

/**
 * Ported from ContactController@send + SendContactMessageRequest. The Turnstile
 * check is skipped (no keys configured yet), same as the register flow.
 */
export async function sendContact(
  _prev: ContactState,
  formData: FormData,
): Promise<ContactState> {
  const name = String(formData.get("name") ?? "").trim();
  const email = String(formData.get("email") ?? "").trim().toLowerCase();
  const message = String(formData.get("message") ?? "").trim();

  if (!name || name.length > 120) return { error: "Enter your name." };
  if (!EMAIL_RE.test(email) || email.length > 255) {
    return { error: "Enter a valid email address." };
  }
  if (!message || message.length > 4000) {
    return { error: "Enter a message (4000 characters max)." };
  }
  if (!(await rateLimit(`contact:${email}`, 5, 60))) {
    return { error: "You're sending messages too fast. Try again in a minute." };
  }

  try {
    await sendMail({
      to: env.CONTACT_EMAIL,
      replyTo: email,
      subject: `Collr contact form: ${name}`,
      text: `From: ${name} (${email})\n\n${message}`,
      html: emailWrap({
        subject: `Collr contact form: ${name}`,
        context: "CONTACT",
        body: `<p style="color:#a9adb4;font-size:15px;line-height:1.65em;margin:0 0 16px;">New message from the contact form:</p>${emailSpecTable([ ["FROM", `${esc(name)} (${esc(email)})` ], ["MESSAGE", esc(message).replace(/\n/g, "<br>") ]])}`,
      }),
    });
  } catch {
    return {
      error:
        "Something went wrong sending your message. Please try again or email us directly.",
    };
  }

  redirect(
    `/contact?status=${encodeURIComponent(
      "Thanks — we've got your message and will get back to you soon.",
    )}`,
  );
}
